import React, { useState, useMemo } from 'react';
import { MdClose, MdExpandMore, MdExpandLess, MdCheck } from 'react-icons/md';

/**
 * Accordion-style tag breakdown for a Representative profile.
 *
 * Top-level (parent) tags are rendered as expandable rows. Each row shows the
 * tag's aggregate match percentage and a visual match bar. Expanding a row
 * reveals the child sub-tags inline. Clicking a row header toggles the entire
 * subtree's selection (parent + children); clicking a child chip toggles just
 * that one tag.
 *
 * Falls back to the flat tagBreakdown view if the backend hasn't supplied the
 * hierarchical `tagGroups` field yet.
 */
function RepTagFilter({
  tagBreakdown,
  tagGroups,
  selectedTags,
  selectedTag,
  onTagClick,
  onTagGroupClick,
  onClearAllTags,
  onShowAllPolls,
  isOwnProfile,
}) {
  // Always declare hooks before any conditional returns to keep hook order stable.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const hasGroups = Array.isArray(tagGroups) && tagGroups.length > 0;
  // Build a quick lookup of which subtrees are fully selected (parent + all children).
  const fullySelectedRoots = useMemo(() => {
    if (!hasGroups) return new Set();
    const out = new Set();
    for (const g of tagGroups) {
      const ids = g.subtree_tag_ids || [g.tag_id, ...(g.children || []).map((c) => c.tag_id)];
      if (ids.length > 0 && ids.every((id) => selectedTags.has(id))) out.add(g.tag_id);
    }
    return out;
  }, [tagGroups, selectedTags, hasGroups]);

  // After hooks, it's safe to bail out for the empty state.
  if (!tagBreakdown || tagBreakdown.length === 0) return null;

  const toggleExpand = (groupId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const matchColor = (pct) => {
    if (pct >= 70) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (pct >= 40) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-rose-700 bg-rose-50 border-rose-200';
  };

  const barColor = (pct) => {
    if (pct >= 70) return 'bg-emerald-500';
    if (pct >= 40) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {isOwnProfile ? 'Positions by Topic' : 'Match by Topic'}
        </h3>
        {selectedTags.size > 0 && (
          <button
            onClick={onClearAllTags}
            className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
            data-testid="clear-all-tags-btn"
          >
            Clear All ({selectedTags.size})
          </button>
        )}
      </div>

      {hasGroups ? (
        <div className="divide-y divide-gray-100" data-testid="tag-groups-list">
          {tagGroups.map((group) => {
            const expanded = expandedIds.has(group.tag_id);
            const fullSelected = fullySelectedRoots.has(group.tag_id);
            const partialSelected =
              !fullSelected &&
              ((group.children || []).some((c) => selectedTags.has(c.tag_id)) ||
                selectedTags.has(group.tag_id));
            const pct = group.match_percentage ?? 0;
            const childCount = (group.children || []).length;
            const subtreeIds = group.subtree_tag_ids || [
              group.tag_id,
              ...(group.children || []).map((c) => c.tag_id),
            ];

            return (
              <div key={group.tag_id} data-testid={`tag-group-${group.tag_id}`}>
                <div
                  className={`flex items-center gap-3 py-3 px-2 rounded-md transition-colors ${
                    fullSelected
                      ? 'bg-primary/5'
                      : partialSelected
                      ? 'bg-amber-50/40'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onTagGroupClick(subtreeIds)}
                    className="flex-1 flex items-center gap-3 text-left"
                    data-testid={`tag-group-select-${group.tag_id}`}
                  >
                    <span
                      className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                        fullSelected
                          ? 'bg-primary border-primary text-white'
                          : partialSelected
                          ? 'bg-white border-amber-400'
                          : 'bg-white border-gray-300'
                      }`}
                      aria-hidden="true"
                    >
                      {fullSelected && <MdCheck className="text-sm" />}
                      {partialSelected && (
                        <span className="block w-2 h-2 rounded-sm bg-amber-500" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="font-rubik font-semibold text-gray-900 text-sm truncate">
                          {group.tag}
                        </span>
                        {childCount > 0 && (
                          <span className="text-[10px] uppercase tracking-wide text-gray-400 font-rubik">
                            {childCount} subtopic{childCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </span>
                      <span className="block mt-1 text-xs text-gray-500 font-rubik">
                        {group.rep_responses} poll{group.rep_responses === 1 ? '' : 's'}
                        {group.total > 0
                          ? ` · you answered ${group.total}`
                          : ' · you haven\u2019t answered any yet'}
                      </span>
                    </span>
                    {!isOwnProfile && (
                      <span className="hidden sm:flex flex-col items-end shrink-0 min-w-[110px]">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-rubik border ${matchColor(pct)}`}
                        >
                          {pct}% match
                        </span>
                        <span className="mt-1.5 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <span
                            className={`block h-full ${barColor(pct)} transition-all`}
                            style={{ width: `${Math.max(2, pct)}%` }}
                          />
                        </span>
                      </span>
                    )}
                  </button>
                  {childCount > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleExpand(group.tag_id)}
                      className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                      aria-label={expanded ? 'Collapse subtopics' : 'Expand subtopics'}
                      data-testid={`tag-group-toggle-${group.tag_id}`}
                    >
                      {expanded ? <MdExpandLess className="text-xl" /> : <MdExpandMore className="text-xl" />}
                    </button>
                  )}
                </div>

                {/* Mobile-only match badge (sm breakpoint hides the inline one) */}
                {!isOwnProfile && (
                  <div className="sm:hidden ml-8 pb-2 -mt-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-rubik border ${matchColor(pct)}`}
                    >
                      {pct}% match
                    </span>
                  </div>
                )}

                {expanded && childCount > 0 && (
                  <div className="pl-8 pr-2 pb-3 flex flex-wrap gap-1.5" data-testid={`tag-group-children-${group.tag_id}`}>
                    {(group.children || []).map((child) => {
                      const cSel = selectedTags.has(child.tag_id);
                      const cPct = child.match_percentage ?? 0;
                      return (
                        <button
                          key={child.tag_id}
                          type="button"
                          onClick={() => onTagClick(child.tag_id, child.tag)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                            cSel
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-primary/40 hover:bg-primary/5'
                          }`}
                          data-testid={`tag-${child.tag_id}`}
                        >
                          {child.tag}
                          {!isOwnProfile && (
                            <span className={`text-[10px] font-bold ${cSel ? 'text-white/80' : 'text-gray-400'}`}>
                              {cPct}%
                            </span>
                          )}
                          <span className={`text-[10px] ${cSel ? 'text-white/60' : 'text-gray-300'}`}>
                            ({child.rep_responses})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Fallback: flat pill view for older API responses without tag_groups.
        <div className="max-h-48 overflow-y-auto pr-1 scrollbar-thin" data-testid="tags-scroll-container">
          <div className="flex flex-wrap gap-2">
            {tagBreakdown.map((tag) => (
              <button
                key={tag.tag_id}
                onClick={() => onTagClick(tag.tag_id, tag.tag)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  selectedTags.has(tag.tag_id)
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-primary/40 hover:bg-primary/5'
                }`}
                data-testid={`tag-${tag.tag_id}`}
              >
                {tag.tag}
                {!isOwnProfile && (
                  <span className={`text-[10px] font-bold ${selectedTags.has(tag.tag_id) ? 'text-white/80' : 'text-gray-400'}`}>
                    {tag.match_percentage}%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedTags.size > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="selected-tags-area">
          {tagBreakdown
            .filter((t) => selectedTags.has(t.tag_id))
            .map((tag) => (
              <span
                key={tag.tag_id}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
              >
                {tag.tag}
                <button
                  onClick={() => onTagClick(tag.tag_id, tag.tag)}
                  className="ml-0.5 hover:text-red-500 transition-colors"
                  data-testid={`remove-tag-${tag.tag_id}`}
                  aria-label={`Remove ${tag.tag} filter`}
                >
                  <MdClose className="text-sm" />
                </button>
              </span>
            ))}
        </div>
      )}

      {!selectedTag && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center" data-testid="tag-select-disclaimer">
          <p className="text-sm text-blue-700 font-medium font-rubik">
            Tap a topic above to see {isOwnProfile ? 'your' : "this representative\u2019s"} positions, or show all at once.
          </p>
        </div>
      )}
      <button
        onClick={onShowAllPolls}
        className={`mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          selectedTag?.tag_id === '__all__'
            ? 'bg-primary text-white'
            : 'border border-primary/30 text-primary hover:bg-primary/10'
        }`}
        data-testid="show-all-polls-btn"
      >
        {selectedTag?.tag_id === '__all__' ? 'Showing All Poll Questions' : 'Show All Poll Questions'}
      </button>
    </div>
  );
}

export default RepTagFilter;
